/**
 * Hora Clock Configuration
 * Circular ring visualization for planetary hour rulers
 */

import { PLANETARY_ORDER, WEEKDAY_LORDS, getPlanetCode } from './muhurtaRulers';
import { PanelSchema, buildDefaults } from './panelSchema';

// ─────────────────────────────────────────────────────────────────────────────
// Design Config Types
// ─────────────────────────────────────────────────────────────────────────────

export type SectorColorMode = 'uniform' | 'gradient' | 'cycle' | 'per-planet';

export interface PlanetColorMap {
  Surya: string;
  Chandra: string;
  Mangala: string;
  Budha: string;
  Guru: string;
  Shukra: string;
  Shani: string;
}

export interface HoraClockDesignConfig {
  // Background
  backgroundColor: string;
  backgroundOpacity: number;

  // Sector coloring
  colorMode: SectorColorMode;
  uniformColor: string;
  gradientColors: string[];
  cycleColors: string[];
  planetColors: PlanetColorMap;

  // Sectors
  sectorOpacity: number;
  sectorHighlightOpacity: number;
  highlightColor: string;
  sectorStrokeWidth: number;
  sectorStrokeOpacity: number;

  // Division lines + borders
  divisionColor: string;
  divisionWidth: number;
  divisionOpacity: number;
  borderColor: string;
  borderWidth: number;
  borderOpacity: number;

  // Labels
  labelSize: string;
  labelColor: string;
  labelHighlightColor: string;
  labelOpacity: number;
  sublabelSize: string;
  sublabelColor: string;
  sublabelOpacity: number;
  sublabelOffset: number;

  // Minute ticks
  tickZone: number;
  majorTickColor: string;
  majorTickWidth: number;
  minorTickColor: string;
  minorTickWidth: number;
  tickOpacity: number;

  // Hands
  minuteHandColor: string;
  minuteHandWidth: number;
  minuteHandOpacity: number;
  mlLineColor: string;
  mlLineWidth: number;
  mlLineOpacity: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Design Schema — Single source of truth for data model, defaults, and UI
// ─────────────────────────────────────────────────────────────────────────────

export const HORA_CLOCK_DESIGN_SCHEMA: PanelSchema = {
  storageKey: 'horaClockDesign',
  eventType: 'hora.clock.design.changed',
  sections: [
    {
      title: 'Background',
      rows: [
        { label: 'Color', fields: [
          { key: 'backgroundColor', type: 'color', default: '#FFFFFF' },
          { key: 'backgroundOpacity', type: 'opacity', default: 1 },
        ]},
      ],
    },
    {
      title: 'Sector Colors',
      rows: [
        { label: 'Mode', fields: [
          { key: 'sectorColorMode', type: 'custom', customId: 'sectorColorMode',
            defaults: {
              colorMode: 'uniform',
              uniformColor: '#E2E8F0',
              gradientColors: [
                '#FED7AA', '#FEFCBF', '#C6F6D5', '#B2F5EA', '#BEE3F8', '#C3DAFE',
                '#E9D8FD', '#FED7E2', '#FEB2B2', '#FDDCAB', '#D6EDCB', '#B5E4F7',
              ],
              cycleColors: ['#E2E8F0', '#FED7AA', '#C6F6D5', '#BEE3F8', '#E9D8FD'],
              planetColors: {
                Surya: '#F6AD55', Chandra: '#E2E8F0', Mangala: '#FC8181',
                Budha: '#68D391', Guru: '#F6E05E', Shukra: '#FED7E2', Shani: '#90CDF4',
              },
            },
          },
        ]},
        { label: 'Highlight', fields: [
          { key: 'highlightColor', type: 'color', default: '#FFA500' },
          { key: 'sectorHighlightOpacity', type: 'opacity', default: 0.35 },
        ]},
        { label: 'Fill', fields: [
          { key: 'sectorOpacity', type: 'opacity', default: 0.12 },
        ]},
        { label: 'Stroke', fields: [
          { key: 'sectorStrokeWidth', type: 'width', default: 0.5, min: 0, max: 3, step: 0.1 },
          { key: 'sectorStrokeOpacity', type: 'opacity', default: 0.4 },
        ]},
      ],
    },
    {
      title: 'Lines',
      rows: [
        { label: 'Division', fields: [
          { key: 'divisionColor', type: 'color', default: '#A0AEC0' },
          { key: 'divisionWidth', type: 'width', default: 0.8 },
          { key: 'divisionOpacity', type: 'opacity', default: 1 },
        ]},
        { label: 'Border', fields: [
          { key: 'borderColor', type: 'color', default: '#A0AEC0' },
          { key: 'borderWidth', type: 'width', default: 1 },
          { key: 'borderOpacity', type: 'opacity', default: 1 },
        ]},
      ],
    },
    {
      title: 'Labels',
      rows: [
        { label: 'Size', fields: [
          { key: 'labelSize', type: 'select', default: '11px',
            options: ['8px', '9px', '10px', '11px', '12px', '14px'] },
        ]},
        { label: 'Colors', fields: [
          { key: 'labelColor', type: 'color', default: '#4A5568', label: 'N' },
          { key: 'labelHighlightColor', type: 'color', default: '#C05621', label: 'H' },
          { key: 'labelOpacity', type: 'opacity', default: 1 },
        ]},
        { label: 'Sublabel', fields: [
          { key: 'sublabelSize', type: 'select', default: '8px',
            options: ['8px', '9px', '10px', '11px', '12px', '14px'] },
          { key: 'sublabelColor', type: 'color', default: '#718096' },
          { key: 'sublabelOpacity', type: 'opacity', default: 1 },
        ]},
      ],
    },
    {
      title: 'Clock',
      rows: [
        { label: 'Minute', fields: [
          { key: 'minuteHandColor', type: 'color', default: '#ED8936' },
          { key: 'minuteHandWidth', type: 'width', default: 1.5 },
          { key: 'minuteHandOpacity', type: 'opacity', default: 1 },
        ]},
        { label: 'ML line', fields: [
          { key: 'mlLineColor', type: 'color', default: '#DD6B20' },
          { key: 'mlLineWidth', type: 'width', default: 2 },
          { key: 'mlLineOpacity', type: 'opacity', default: 1 },
        ]},
        { label: 'Ticks', fields: [
          { key: 'majorTickColor', type: 'color', default: '#718096', label: '5m' },
          { key: 'minorTickColor', type: 'color', default: '#A0AEC0', label: '1m' },
          { key: 'tickOpacity', type: 'opacity', default: 1 },
        ]},
      ],
    },
  ],
  hiddenDefaults: {
    sublabelOffset: 14,
    tickZone: 0.3,
    majorTickWidth: 0.8,
    minorTickWidth: 0.4,
  },
  presets: [
    { name: 'Classic', values: {} },
    { name: 'Vedic Colors', values: {
      colorMode: 'per-planet', sectorOpacity: 0.25,
      highlightColor: '#DD6B20', labelColor: '#2D3748', labelHighlightColor: '#9C4221',
    }},
    { name: 'Dark', values: {
      backgroundColor: '#1A202C', backgroundOpacity: 1,
      uniformColor: '#2D3748', sectorOpacity: 0.3, sectorHighlightOpacity: 0.5,
      highlightColor: '#ED8936', divisionColor: '#4A5568', borderColor: '#4A5568',
      labelColor: '#A0AEC0', labelHighlightColor: '#F6AD55', sublabelColor: '#718096',
      majorTickColor: '#4A5568', minorTickColor: '#2D3748',
    }},
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Default Design (derived from schema — single source of truth)
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_HORA_CLOCK_DESIGN = buildDefaults(HORA_CLOCK_DESIGN_SCHEMA) as HoraClockDesignConfig;

// ─────────────────────────────────────────────────────────────────────────────
// Sector Color Resolver
// ─────────────────────────────────────────────────────────────────────────────

export function resolveHoraSectorColor(
  design: HoraClockDesignConfig,
  sectorIndex: number,
  planetName: string,
  isHighlighted: boolean
): string {
  if (isHighlighted) return design.highlightColor;

  switch (design.colorMode) {
    case 'uniform':
      return design.uniformColor;
    case 'gradient':
      return design.gradientColors[sectorIndex % design.gradientColors.length] || design.uniformColor;
    case 'cycle':
      return design.cycleColors[sectorIndex % design.cycleColors.length] || design.uniformColor;
    case 'per-planet':
      return (design.planetColors as any)[planetName] || design.uniformColor;
    default:
      return design.uniformColor;
  }
}

/**
 * Configuration for a single circular ring
 * Generic interface — supports hora, nakshatra, 60-sector, and future rings
 */
export interface CircularRingConfig {
  type: string;
  name: string;
  sectors: number;
  innerRadius: number;
  outerRadius: number;
  direction: 'clockwise' | 'counterclockwise';
  colorScheme: string;
}

/**
 * Center of the 700x700 SVG viewBox
 */
export const RING_CENTER_X = 350;
export const RING_CENTER_Y = 350;

/** Margin from viewBox edge for radial line labels */
const RING_LABEL_MARGIN = 20;
/** outerRadius — as large as fits with label margin */
const HORA_OUTER_R = Math.min(RING_CENTER_X, RING_CENTER_Y) - RING_LABEL_MARGIN;
/** innerRadius — proportional to outer (0.67 ratio preserves readable ring width) */
const HORA_INNER_R = Math.round(HORA_OUTER_R * 0.67);

/**
 * Hora ring: 12 sectors for 12 planetary hours
 * Dynamically sized to fill the 700x700 viewBox
 * First sector at 12 o'clock, clockwise (like a clock face)
 */
export const HORA_RING_CONFIG: CircularRingConfig = {
  type: 'hora',
  name: 'Hora Clock',
  sectors: 12,
  innerRadius: HORA_INNER_R,
  outerRadius: HORA_OUTER_R,
  direction: 'clockwise',
  colorScheme: 'orange',
};

/**
 * Hora ring: 24 sectors for full sunrise-to-sunrise view
 * Each sector = 15° (360/24), all horas on one clock face
 */
export const HORA_RING_CONFIG_24: CircularRingConfig = {
  type: 'hora',
  name: 'Hora Clock 24h',
  sectors: 24,
  innerRadius: HORA_INNER_R,
  outerRadius: HORA_OUTER_R,
  direction: 'clockwise',
  colorScheme: 'orange',
};

/**
 * Calculate 12 hora rulers from the vara lord (ruling planet of the day)
 *
 * The 24-hour sequence follows PLANETARY_ORDER from muhurtaRulers.ts,
 * starting from the vara lord at hour 1 (sunrise).
 * - Day hours (1-12): offset 0 from vara lord
 * - Night hours (13-24): offset 12 from vara lord (continues the sequence)
 *
 * @param varaLord - Ruling planet name from API (e.g. 'Shukra', 'Mangala')
 * @param hourOffset - 0 for day hours (1-12), 12 for night hours (13-24)
 * @returns Array of 12 hora rulers with full name and 2-char code
 */
export function getHoraRulers(varaLord: string, hourOffset: number = 0): Array<{ ruler: string; code: string }> {
  const startIndex = PLANETARY_ORDER.indexOf(varaLord);
  if (startIndex === -1) {
    throw new Error(`Unknown vara lord: ${varaLord}. Expected one of: ${PLANETARY_ORDER.join(', ')}`);
  }

  const rulers: Array<{ ruler: string; code: string }> = [];

  for (let i = 0; i < 12; i++) {
    const ruler = PLANETARY_ORDER[(startIndex + hourOffset + i) % 7];
    rulers.push({ ruler, code: getPlanetCode(ruler) });
  }

  return rulers;
}
